import React, { useEffect, useState } from 'react';
import styles from './SelectCoffeeBean.module.scss';
import CardSelector, {
  CardOption,
} from '../../../components/molecules/CardSelector/CardSelector';
import { useOrderFlowStore, BeanType, BeanVariety } from '../../../store/orderFlowStore';
import { useTranslations } from '../../../hooks/useTranslations';
import { useCurrentLanguage } from '../../../hooks/useCurrentLanguage';
import { useMarketInfo, formatPrice } from '../../../hooks/useMarketInfo';
import { ProductFlowMapping, SugarConcentration, PackageType } from '../../../types/orderFlow';
import SelectionGroup, { SelectionOption } from '../../../components/molecules/SelectionGroup/SelectionGroup';
import { useCustomerInfo } from '../../../hooks/useCustomerInfo';
import { useOptionIcons } from '../../../hooks/useOptionIcons';
import { useTemporaryRestrictions } from '../../../hooks/useTemporaryRestrictions';

/**
 * STEP2: 基本仕様の選択コンポーネント
 * 
 * フロー別の選択項目:
 * - フリーズドライ: 豆種類 + インナーパッケージ
 * - 濃縮液: パッキング種類 + 豆種類 + 砂糖濃度
 * - ストレートコーヒー: 豆種類 + 砂糖濃度
 * 
 * Liquidテンプレートから渡されるデータ構造例:
 * window.wakoCustomFlowData = {
 *   mappings: [
 *     {
 *       targetProductHandle: "freeze-dried-house-blend-stick",
 *       productType: "freeze-dried",  // Liquid側でタグから生成
 *       beanType: "wako-house-blend", // Liquid側でタグから生成
 *       packageType: "stick",         // Liquid側でタグから生成
 *       optionSetReference: {
 *         setIdentifier: "fd-set-v1",
 *         fdInnerDesignStick: [{ id: "...", name: "インナーデザイン", price: 5000, variantId: "..." }],
 *         fdOuterBox: [{ id: "...", name: "外箱", price: 3000, variantId: "..." }]
 *       }
 *     }
 *   ]
 * }
 */

interface BeanVarietyInfo {
  value: BeanVariety;
  label: string;
  description: string;
  priceInfo: string;
  image?: string;
}

interface SugarConcentrationInfo {
  value: SugarConcentration;
  label: string;
  description: string;
  priceInfo?: string;
}

interface PackageTypeInfo {
  value: PackageType;
  label: string;
  description: string;
  image?: string;
}

const SelectCoffeeBean: React.FC = () => {
  const { 
    productType,
    beanType, 
    setBeanType, 
    selectedBean, 
    setSelectedBean,
    packageType,
    setPackageType,
    sugarConcentration,
    setSugarConcentration,
    lotSize,
    availableProducts,
    currentProduct,
    updateCurrentProduct,
    calculateTotalPrice
  } = useOrderFlowStore();
  
  const t = useTranslations();
  const currentLanguage = useCurrentLanguage();
  const marketInfo = useMarketInfo();
  const isJapan = currentLanguage !== 'en'; // 英語以外は国内版とみなす
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const customerInfo = useCustomerInfo();
  const { getOptionIcon, isOptionIconsAvailable } = useOptionIcons();
  const {
    isSpoutDisabled,
    isBottleDisabled,
    isSugarDisabled,
    disabledMessage,
  } = useTemporaryRestrictions();

  // コンポーネントマウント時の初期化
  useEffect(() => {
    updateCurrentProduct();
    calculateTotalPrice();
  }, [updateCurrentProduct, calculateTotalPrice]);

  // 制約に基づく自動選択
  // 既に非活性なオプションが選択されている場合もフォールバック値を設定
  useEffect(() => {
    if (!productType) return;

    // 濃縮液: スパウト非活性 → pouch自動選択（既にspoutが選択されている場合も含む）
    if (productType === 'liquid-concentrate' && isSpoutDisabled) {
      if (!packageType || packageType === 'spout') {
        setPackageType('pouch');
      }
    }

    // ストレートコーヒー: ボトル非活性 → spout自動選択（既にbottleが選択されている場合も含む）
    if (productType === 'straight-coffee' && isBottleDisabled) {
      if (!packageType || packageType === 'bottle') {
        setPackageType('spout');
      }
    }

    // ストレートコーヒー: 砂糖非活性 → 無糖自動選択（既に他の砂糖濃度が選択されている場合も含む）
    if (productType === 'straight-coffee' && isSugarDisabled) {
      if (!sugarConcentration || sugarConcentration !== 'none') {
        setSugarConcentration('none');
      }
    }
  }, [
    productType,
    packageType,
    sugarConcentration,
    isSpoutDisabled,
    isBottleDisabled,
    isSugarDisabled,
    setPackageType,
    setSugarConcentration,
  ]);

  // 選択項目が変更された時の処理
  useEffect(() => {
    if (beanType === 'my-bean') {
      // 自分の豆を選択した場合、銘柄選択をクリア
      setSelectedBean(null);
    }
    updateCurrentProduct();
    calculateTotalPrice();
  }, [beanType, packageType, sugarConcentration, setSelectedBean, updateCurrentProduct, calculateTotalPrice]);

  // 利用可能な商品から銘柄オプションを生成
  const getBeanVarietyOptions = (): BeanVarietyInfo[] => {
    if (!productType || beanType !== 'wako') return [];

    // WAKOの豆の場合、利用可能な銘柄を取得
    const wakoProducts = availableProducts.filter(product => 
      product.productType === productType &&
      product.beanType === 'wako'
    );

    const varieties: BeanVarietyInfo[] = [];
    const addedVarieties = new Set<string>();

    wakoProducts.forEach(product => {
      // 商品タグから銘柄を判定
      const beanTag = product.productInfo.tags.find(tag => tag.startsWith('flow_bean_type:'));
      if (beanTag) {
        const beanValue = beanTag.split(':')[1] as BeanVariety;
        
        if (!addedVarieties.has(beanValue)) {
          addedVarieties.add(beanValue);
          
          // 銘柄情報を構築
          let varietyInfo: BeanVarietyInfo;
          
          switch (beanValue) {
            case 'wako-house-blend':
              varietyInfo = {
                value: beanValue,
                label: t.selectCoffeeBean_bean1_label,
                description: t.selectCoffeeBean_bean1_desc,
                priceInfo: t.selectCoffeeBean_bean1_price || '',
                // image: window.wakoCustomFlowData?.previewImages?.bean_wako_house_blend
              };
              break;
            case 'wako-house-dark-blend':
              varietyInfo = {
                value: beanValue,
                label: t.selectCoffeeBean_bean2_label,
                description: t.selectCoffeeBean_bean2_desc,
                priceInfo: t.selectCoffeeBean_bean2_price || '',
                // image: window.wakoCustomFlowData?.previewImages?.bean_wako_house_dark_blend
              };
              break;
            default:
              varietyInfo = {
                value: beanValue,
                label: beanValue,
                description: '',
                priceInfo: '',
              };
          }
          
          varieties.push(varietyInfo);
        }
      }
    });

    return varieties;
  };

  // パッキング種類オプション（濃縮液・ストレート用）
  const getPackageTypeOptions = (): (PackageTypeInfo & { disabled?: boolean; disabledMessage?: string })[] => {
    if (productType === 'freeze-dried') return [];

    if (productType === 'liquid-concentrate') {
      return [
        {
          value: 'spout',
          label: t.selectPackage_spout_label,
          description: t.selectPackage_spout_desc,
          image: isOptionIconsAvailable() ? getOptionIcon('package', 'spout', 'liquid-concentrate') : undefined,
          // 濃縮液のスパウトを非活性
          disabled: isSpoutDisabled,
          disabledMessage: isSpoutDisabled ? disabledMessage : undefined,
        },
        {
          value: 'pouch',
          label: t.selectPackage_pouch_label,
          description: t.selectPackage_pouch_desc,
          image: isOptionIconsAvailable() ? getOptionIcon('package', 'pouch', 'liquid-concentrate') : undefined,
        }
      ];
    }

    // ストレートコーヒーの場合
    if (productType === 'straight-coffee') {
      // 基本選択肢（スパウト）- 国内/海外共通
      const options: (PackageTypeInfo & { disabled?: boolean; disabledMessage?: string })[] = [
        {
          value: 'spout',
          label: t.selectPackage_spout_label,
          description: t.selectPackage_spout_desc,
          image: isOptionIconsAvailable() ? getOptionIcon('package', 'spout', 'straight-coffee') : undefined,
        }
      ];
      
      // 国内版のみボトル選択肢を追加
      if (isJapan) {
        options.push({
          value: 'bottle',
          label: t.selectPackage_bottle_label,
          description: t.selectPackage_bottle_desc,
          image: isOptionIconsAvailable() ? getOptionIcon('package', 'bottle', 'straight-coffee') : undefined,
          // ストレートコーヒーのボトルを非活性
          disabled: isBottleDisabled,
          disabledMessage: isBottleDisabled ? disabledMessage : undefined,
        });
      }
      
      return options;
    }

    return [];
  };

  // 砂糖濃度オプション（濃縮液・ストレート用）
  const getSugarConcentrationOptions = (): (SugarConcentrationInfo & { disabled?: boolean; disabledMessage?: string })[] => {
    if (productType === 'freeze-dried') return [];

    // 砂糖濃度アドオン商品の価格情報を取得
    const getSugarPrice = (concentration: SugarConcentration): string => {
      // 非ログインユーザーには価格を表示しない
      if (!customerInfo.isLoggedIn) {
        return '';
      }
      
      if (!currentProduct?.optionSetReference) return '';
      
      const sugarOptions = productType === 'liquid-concentrate' 
        ? currentProduct.optionSetReference.nk_sugar 
        : currentProduct.optionSetReference.st_sugar;
      
      if (!sugarOptions) return '';
      
      // 現在選択されているロット数に対応する価格を取得
      // ロット数が未選択の場合は最小ロットの価格を表示
      const targetLot = lotSize || '100';
      const sugarOption = sugarOptions.find((option: any) => 
        option.title && option.title.includes(concentration) && option.title.includes(targetLot)
      );
      
      if (sugarOption && Number(sugarOption.price) > 0) {
        return `+${formatPrice(Number(sugarOption.price), { showSymbol: true, showCurrency: false })}`;
      }
      
      return '';
    };

    return [
      {
        value: 'none',
        label: t.selectSugar_none_label,
        description: t.selectSugar_none_desc,
        priceInfo: getSugarPrice('none'),
        // 無糖は常に活性（有糖オプションのみ非活性にする）
        disabled: false,
        disabledMessage: undefined,
      },
      {
        value: '3',
        label: t.selectSugar_3_label,
        description: t.selectSugar_3_desc,
        priceInfo: getSugarPrice('3'),
        disabled: isSugarDisabled,
        disabledMessage: isSugarDisabled ? disabledMessage : undefined,
      },
      {
        value: '6',
        label: t.selectSugar_6_label,
        description: t.selectSugar_6_desc,
        priceInfo: getSugarPrice('6'),
        disabled: isSugarDisabled,
        disabledMessage: isSugarDisabled ? disabledMessage : undefined,
      },
      {
        value: '9',
        label: t.selectSugar_9_label,
        description: t.selectSugar_9_desc,
        priceInfo: getSugarPrice('9'),
        disabled: isSugarDisabled,
        disabledMessage: isSugarDisabled ? disabledMessage : undefined,
      },
      {
        value: '12',
        label: t.selectSugar_12_label,
        description: t.selectSugar_12_desc,
        priceInfo: getSugarPrice('12'),
        disabled: isSugarDisabled,
        disabledMessage: isSugarDisabled ? disabledMessage : undefined,
      },
      {
        value: '18',
        label: t.selectSugar_18_label,
        description: t.selectSugar_18_desc,
        priceInfo: getSugarPrice('18'),
        disabled: isSugarDisabled,
        disabledMessage: isSugarDisabled ? disabledMessage : undefined,
      },
      {
        value: '24',
        label: t.selectSugar_24_label,
        description: t.selectSugar_24_desc,
        priceInfo: getSugarPrice('24'),
        disabled: isSugarDisabled,
        disabledMessage: isSugarDisabled ? disabledMessage : undefined,
      }
    ];
  };

  // インナーパッケージオプション（フリーズドライ用）
  const getInnerPackageOptions = (): PackageTypeInfo[] => {
    if (productType !== 'freeze-dried') return [];

    return [
      {
        value: 'stick',
        label: t.selectInnerPackage_stick_label,
        description: t.selectInnerPackage_stick_desc,
        image: isOptionIconsAvailable() ? getOptionIcon('package', 'stick', 'freeze-dried') : undefined,
      },
      {
        value: 'cube',
        label: t.selectInnerPackage_cube_label,
        description: t.selectInnerPackage_cube_desc,
        image: isOptionIconsAvailable() ? getOptionIcon('package', 'cube', 'freeze-dried') : undefined,
      }
    ];
  };

  // 価格情報の動的取得
  const getPriceInfo = (variety: BeanVariety): string => {
    // 非ログインユーザーには価格を表示しない
    if (!customerInfo.isLoggedIn) {
      return '';
    }
    
    if (!currentProduct) return '';
    
    // ベース価格情報を表示（実際の価格はロット選択後に確定）
    const firstVariant = currentProduct.productInfo.variants[0];
    if (firstVariant) {
      const basePrice = parseFloat(firstVariant.price);
      return `(${formatPrice(basePrice, { showSymbol: true, showCurrency: false })}〜/${t.priceDisplay_perLot})`;
    }
    
    return '';
  };

  // 豆タイプ選択オプション
  const beanTypeOptions: CardOption[] = [
    {
      value: 'my-bean',
      label: t.selectCoffeeBean_option1_label,
      description: t.selectCoffeeBean_option1_desc,
      priceInfo: '', // 自分の豆に価格情報は不要
      // image: window.wakoCustomFlowData?.previewImages?.bean_own
    },
    {
      value: 'wako',
      label: t.selectCoffeeBean_option2_label,
      description: t.selectCoffeeBean_option2_desc,
      priceInfo: '', // WAKOの豆の価格は銘柄で決まる
      // image: window.wakoCustomFlowData?.previewImages?.bean_wako
    },
  ];

  // カードセレクター用オプション変換
  const beanVarietyCardOptions: CardOption[] = getBeanVarietyOptions().map(variety => ({
    value: variety.value,
    label: variety.label,
    priceInfo: variety.priceInfo || getPriceInfo(variety.value),
    description: variety.description,
    image: variety.image,
  }));

  const packageTypeCardOptions: CardOption[] = getPackageTypeOptions().map(pkg => ({
    value: pkg.value,
    label: pkg.label,
    description: pkg.description,
    priceInfo: '',
    image: pkg.image,
    disabled: pkg.disabled,
    disabledMessage: pkg.disabledMessage,
  }));

  const innerPackageCardOptions: CardOption[] = getInnerPackageOptions().map(pkg => ({
    value: pkg.value,
    label: pkg.label,
    description: pkg.description,
    priceInfo: '',
    image: pkg.image,
  }));

  const sugarConcentrationCardOptions: CardOption[] = getSugarConcentrationOptions().map(sugar => ({
    value: sugar.value,
    label: sugar.label,
    description: sugar.description,
    priceInfo: sugar.priceInfo || '',
    disabled: sugar.disabled,
    disabledMessage: sugar.disabledMessage,
  }));

  // バリデーション
  const isValid = (): boolean => {
    if (!beanType) return false;
    if (beanType === 'wako' && !selectedBean) return false;
    
    // フリーズドライの場合はインナーパッケージが必要
    if (productType === 'freeze-dried' && !packageType) return false;
    
    // 濃縮液の場合はパッキング種類と砂糖濃度が必要
    if (productType === 'liquid-concentrate') {
      if (!packageType || !sugarConcentration) return false;
    }
    
    // ストレートコーヒーの場合はパッキング種類と砂糖濃度が必要
    if (productType === 'straight-coffee') {
      if (!packageType || !sugarConcentration) return false;
    }
    
    return true;
  };

  // 各種変更ハンドラー
  const handleBeanTypeChange = async (value: string) => {
    setError(null);
    setLoading(true);
    
    try {
      setBeanType(value as BeanType);
    } catch (err) {
      setError(t.error_priceCalculation);
      console.error('Bean type selection error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBeanVarietyChange = async (value: string) => {
    setError(null);
    setLoading(true);
    
    try {
      setSelectedBean(value as BeanVariety);
    } catch (err) {
      setError(t.error_priceCalculation);
      console.error('Bean variety selection error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePackageTypeChange = async (value: string) => {
    setError(null);
    setLoading(true);
    
    try {
      setPackageType(value as PackageType);
    } catch (err) {
      setError(t.error_priceCalculation);
      console.error('Package type selection error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSugarConcentrationChange = async (value: string) => {
    setError(null);
    setLoading(true);
    
    try {
      setSugarConcentration(value as SugarConcentration);
    } catch (err) {
      setError(t.error_priceCalculation);
      console.error('Sugar concentration selection error:', err);
    } finally {
      setLoading(false);
    }
  };

  // 現在の選択状態の表示
  // const renderSelectionSummary = () => {
  //   if (!beanType) return null;
    
  //   return (
  //     <div className={styles.selectionSummary}>
  //       <h4>{t.selectCoffeeBean_currentSelection}</h4>
  //       <ul>
  //         <li>{t.selectCoffeeBean_beanType} {beanType === 'my-bean' ? t.selectCoffeeBean_ownBean : t.selectCoffeeBean_wakoBean}</li>
  //         {beanType === 'wako' && selectedBean && (
  //           <li>{t.selectCoffeeBean_variety} {beanVarietyCardOptions.find(opt => opt.value === selectedBean)?.label}</li>
  //         )}
  //         {productType === 'freeze-dried' && packageType && (
  //           <li>{t.selectCoffeeBean_innerPackage} {innerPackageCardOptions.find(opt => opt.value === packageType)?.label}</li>
  //         )}
  //         {productType === 'liquid-concentrate' && packageType && (
  //           <li>{t.selectCoffeeBean_packaging} {packageTypeCardOptions.find(opt => opt.value === packageType)?.label}</li>
  //         )}
  //         {(productType === 'liquid-concentrate' || productType === 'straight-coffee') && sugarConcentration && (
  //           <li>{t.selectCoffeeBean_sugarConcentration} {sugarConcentrationCardOptions.find(opt => opt.value === sugarConcentration)?.label}</li>
  //         )}
  //       </ul>
  //     </div>
  //   );
  // };

  // 豆選択のオプションを生成（SelectionGroup用）
  const getBeanSelectionOptions = (): SelectionOption[] => {
    const beanOptions: SelectionOption[] = [
      {
        value: 'my-bean',
        label: t.selectCoffeeBean_ownBean_label,
        description: t.selectCoffeeBean_ownBean_desc,
        image: isOptionIconsAvailable() ? getOptionIcon('bean', 'my-bean') : undefined,
      }
    ];

    // WAKOの豆オプション（アコーディオンコンテンツ付き）
    const wakoBeanOption: SelectionOption = {
      value: 'wako',
      label: t.selectCoffeeBean_wakoBean_label,
      description: t.selectCoffeeBean_wakoBean_desc,
      image: isOptionIconsAvailable() ? getOptionIcon('bean', 'wako-bean') : undefined,
      accordionContent: (
        <div className={styles.accordionContent}>
          <SelectionGroup
            title={t.selectCoffeeBean_varietyTitle}
            name="beanVariety"
            options={getBeanVarietySelectionOptions()}
            selectedValue={selectedBean || ''}
            onChange={handleBeanVarietyChange}
            showQuestionIcon={true}
            helpText={t.help_selectCoffeeBeanVariety}
            isNested={true}
          />
        </div>
      )
    };

    beanOptions.push(wakoBeanOption);
    return beanOptions;
  };

  // 銘柄選択のオプションを生成（SelectionGroup用）
  const getBeanVarietySelectionOptions = (): SelectionOption[] => {
    // WAKOの豆が選択されている場合のみ銘柄選択肢を表示
    // beanTypeの状態に依存せず、直接WAKOの豆の選択肢を生成
    if (!productType) return [];

    // WAKOの豆の場合、利用可能な銘柄を取得
    const wakoProducts = availableProducts.filter(product => 
      product.productType === productType &&
      product.beanType === 'wako'
    );

    const varieties: SelectionOption[] = [];
    const addedVarieties = new Set<string>();

    wakoProducts.forEach(product => {
      // 商品タグから銘柄を判定
      const beanTag = product.productInfo.tags.find(tag => tag.startsWith('flow_bean_type:'));
      if (beanTag) {
        const beanValue = beanTag.split(':')[1] as BeanVariety;
        
        if (!addedVarieties.has(beanValue)) {
          addedVarieties.add(beanValue);
          
          // 銘柄情報を構築
          let varietyInfo: SelectionOption;
          
          switch (beanValue) {
            case 'wako-house-blend':
              varietyInfo = {
                value: beanValue,
                label: t.selectCoffeeBean_bean1_label,
                description: t.selectCoffeeBean_bean1_desc,
                image: isOptionIconsAvailable() ? getOptionIcon('bean', 'wako-house-blend') : undefined,
              };
              break;
            case 'wako-house-dark-blend':
              varietyInfo = {
                value: beanValue,
                label: t.selectCoffeeBean_bean2_label,
                description: t.selectCoffeeBean_bean2_desc,
                image: isOptionIconsAvailable() ? getOptionIcon('bean', 'wako-house-dark-blend') : undefined,
              };
              break;
            default:
              varietyInfo = {
                value: beanValue,
                label: beanValue,
                description: '',
              };
          }
          
          varieties.push(varietyInfo);
        }
      }
    });

    // デバッグ用：利用可能な商品がない場合はダミーデータを返す
    if (varieties.length === 0) {
      return [
        {
          value: 'wako-house-blend',
          label: t.selectCoffeeBean_bean1_label,
          description: t.selectCoffeeBean_bean1_desc,
          image: isOptionIconsAvailable() ? getOptionIcon('bean', 'wako-house-blend') : undefined,
        },
        {
          value: 'wako-house-dark-blend',
          label: t.selectCoffeeBean_bean2_label,
          description: t.selectCoffeeBean_bean2_desc,
          image: isOptionIconsAvailable() ? getOptionIcon('bean', 'wako-house-dark-blend') : undefined,
        }
      ];
    }

    return varieties;
  };

  return (
    <div className={styles.container}>
      {/* エラー表示 */}
      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      {/* 濃縮液・ストレートコーヒーの場合：パッキング種類選択 */}
      {(productType === 'liquid-concentrate' || productType === 'straight-coffee') && (
        <div className={styles.section}>
          <CardSelector
            title={t.selectPackage_title}
            options={getPackageTypeOptions().map(option => ({
              value: option.value,
              label: option.label,
              description: option.description,
              priceInfo: '', // 価格情報は別途表示されるため空文字を設定
              image: option.image,
              disabled: option.disabled,
              disabledMessage: option.disabledMessage,
            }))}
            selectedValue={packageType || ''}
            onChange={handlePackageTypeChange}
            showQuestionIcon={true}
            helpText={productType === 'liquid-concentrate' ? (t.help_selectPackageLiquidConcentrate || '') : (t.help_selectPackageStraightCoffee || '')}
          />
          
          {/* 海外版の場合の地域制約説明文 */}
          {productType === 'straight-coffee' && !isJapan && (
            <div className={styles.regionNotice}>
              {t.selectPackage_region_restriction_notice}
            </div>
          )}
        </div>
      )}

      {/* 豆選択セクション */}
      <div className={styles.section}>
        <SelectionGroup
          title={t.selectCoffeeBean_title}
          name="beanSelection"
          options={getBeanSelectionOptions()}
          selectedValue={beanType || ''}
          onChange={handleBeanTypeChange}
          showQuestionIcon={true}
          helpText={t.help_selectCoffeeBean}
        />
      </div>

      {/* フリーズドライの場合：インナーパッケージ選択 */}
      {productType === 'freeze-dried' && (
        <div className={styles.section}>
          <CardSelector
            title={t.selectInnerPackage_title}
            options={innerPackageCardOptions}
            selectedValue={packageType || ''}
            onChange={handlePackageTypeChange}
            showQuestionIcon={true}
            helpText={t.help_selectPackageFreezeDried || ''}
          />
        </div>
      )}

      {/* 濃縮液・ストレートの場合：砂糖濃度選択 */}
      {(productType === 'liquid-concentrate' || productType === 'straight-coffee') && (
        <div className={styles.section}>
          <CardSelector
            title={t.selectSugar_title}
            options={sugarConcentrationCardOptions}
            selectedValue={sugarConcentration || ''}
            onChange={handleSugarConcentrationChange}
            showQuestionIcon={true}
            helpText={t.help_selectSugar || ''}
          />
        </div>
      )}

      {/* 選択状況サマリー */}
      {/* {renderSelectionSummary()} */}

      {/* バリデーション結果 */}

      {/* ローディング状態 */}
      {loading && (
        <div className={styles.loadingMessage}>
          読み込み中...
        </div>
      )}
    </div>
  );
};

export default SelectCoffeeBean; 